import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar, Icon, type SidebarGroup } from "@conuti-das/prince-ui";
import summary from "@/data/generated/summary.json";

type Summary = {
  sources_count: number;
  repos_count: number;
  skills_count: number;
  goals_count?: number;
  runs_count: number;
  ticket_runs_count: number;
  review_tickets_total: number;
  changelog_entries_count?: number;
  artifact_counts: Record<string, Record<string, number>>;
};

export function AppSideNavigation() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const s = summary as Summary;
  const totalArtifacts = Object.values(s.artifact_counts || {})
    .flatMap((r) => Object.values(r))
    .reduce((a, b) => a + b, 0);

  const isActive = (path: string, exact = false) =>
    exact ? pathname === path : pathname === path || pathname.startsWith(path + "/");

  /**
   * Build prince-ui SidebarGroup[] from the flat nav list.
   *
   * The "Regelwerk" section with sub-items is modelled as a collapsible group
   * whose header label is the group title (no clickable parent item itself —
   * the Sidebar API works at item level).  All items navigate via `onSelect`.
   */
  const groups: SidebarGroup[] = useMemo(() => [
    {
      // Top-level items (no group label)
      items: [
        {
          id: "/",
          label: "Executive",
          icon: <Icon name="bolt" size={16} />,
        },
        {
          id: "/architecture",
          label: `Architecture · ${s.repos_count}`,
          icon: <Icon name="grid" size={16} />,
        },
        {
          // Der einzige Ort mit den Atlassian-Zielen — steht bei den
          // Plattform-Sichten, nicht im Regelwerk-Block.
          id: "/umsetzungsgrad",
          label: `Umsetzungsgrad · ${s.goals_count ?? 0}`,
          icon: <Icon name="check" size={16} />,
        },
        {
          id: "/artifacts",
          label: `Artefakte · ${totalArtifacts}`,
          icon: <Icon name="file-text" size={16} />,
        },
        {
          // Ein Eintrag statt drei: Runs, Queue und Autoruns sind eine
          // Zeitachse. Die Zahl ist die der erfassten Läufe — das Einzige,
          // was ohne laufenden Runner überhaupt zählbar ist.
          id: "/activity",
          label: `Aktivität · ${s.runs_count}`,
          icon: <Icon name="clock" size={16} />,
        },
        {
          // Eigene Quelle (`ticket-runs.json`), eigene Aufgabe — bleibt.
          id: "/review",
          label: `Review-Queue · ${s.review_tickets_total ?? 0}`,
          icon: <Icon name="search" size={16} />,
        },
      ],
    },
    {
      label: "Regelwerk",
      collapsible: true,
      items: [
        {
          id: "/changelog",
          label: `Änderungen · ${s.changelog_entries_count ?? 0}`,
          icon: <Icon name="file-text" size={16} />,
        },
        {
          id: "/regelwerk/vergleich",
          label: "AHB-Vergleich",
          icon: <Icon name="chevron-right" size={16} />,
        },
        {
          id: "/regelwerk/tabellen",
          label: "AHB/MIG-Tabellen",
          icon: <Icon name="chevron-right" size={16} />,
        },
        {
          id: "/regelwerk/bedingungen",
          label: "Bedingungsbaum",
          icon: <Icon name="chevron-right" size={16} />,
        },
        {
          id: "/regelwerk/zeitleiste",
          label: "Format-Zeitleiste",
          icon: <Icon name="chevron-right" size={16} />,
        },
      ],
    },
    {
      // More top-level items below the Regelwerk group
      items: [
        {
          id: "/validator",
          label: "Validator",
          icon: <Icon name="check" size={16} />,
        },
        {
          id: "/mapping",
          label: "Mapping",
          icon: <Icon name="link" size={16} />,
        },
        {
          id: "/ebd",
          label: "EBD-Diagramme",
          icon: <Icon name="grid" size={16} />,
        },
      ],
    },
  ], [s.repos_count, s.goals_count, totalArtifacts, s.runs_count, s.review_tickets_total, s.changelog_entries_count]);

  /**
   * Determine the active sidebar item key: longest matching path prefix.
   * Exact match only for "/" to avoid false positives.
   */
  const selectedKey = useMemo(() => {
    // Ein Lauf gehört zur Zeitachse, auch wenn sein Pfad noch `/runs/…` heißt:
    // sonst verlöre die Seitenleiste beim Öffnen eines Laufs jede Markierung.
    if (pathname === "/runs" || pathname.startsWith("/runs/")) return "/activity";
    const allIds = [
      "/",
      "/architecture",
      "/umsetzungsgrad",
      "/artifacts",
      "/activity",
      "/review",
      "/changelog",
      "/regelwerk/vergleich",
      "/regelwerk/tabellen",
      "/regelwerk/bedingungen",
      "/regelwerk/zeitleiste",
      "/validator",
      "/mapping",
      "/ebd",
    ];
    let best = "";
    for (const id of allIds) {
      if (isActive(id, id === "/") && id.length > best.length) {
        best = id;
      }
    }
    return best || undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Sidebar
      groups={groups}
      selectedKey={selectedKey}
      onSelect={(id) => nav(id)}
    />
  );
}
